import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Sparkles, AlertTriangle, Mail, HardDrive, BookOpen, Ticket, Calendar } from 'lucide-react';
import { DATA_DOMAINS, DOMAIN_COLUMNS, DOMAIN_FILTERS, DOMAIN_RECORDS, DataDomain, DataRecord, DomainKey, FilterState, semanticResults } from './data/types';
import { DataTable } from './data/DataTable';
import { FilterPills } from './data/FilterPills';
import { SemanticResults } from './data/SemanticResults';
import { RecordPanel } from './data/RecordPanel';

// Data — the unified records browser. Five domain tabs (Email · Drive ·
// Confluence · Jira · Calendar), table-first layout with common columns
// (title, date, synced, source link) plus domain-specific columns,
// per-domain filter pills, search (semantic on content-indexed domains),
// cursor pagination, and a row-detail slide-over panel.
//
// Mirrors the real app's domain mapping (web/src/lib/data-domains.ts) and
// the dataRecords:listPage pagination contract.

const DOMAIN_ICONS: Record<DomainKey, React.FC<{
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}>> = {
  email: Mail,
  drive: HardDrive,
  confluence: BookOpen,
  jira: Ticket,
  calendar: Calendar
};
const INITIAL_PAGE_SIZE = 5;
const STALE_BANNERS: Partial<Record<DomainKey, string>> = {
  email: 'Email sync has been stalled since 22 Apr — records below may be out of date.',
  confluence: 'Confluence sync has been stalled since 22 Apr — records below may be out of date.'
};
export function DataView() {
  const [activeKey, setActiveKey] = useState<DomainKey>('email');
  const [search, setSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState<FilterState>({});
  const [selected, setSelected] = useState<DataRecord | null>(null);
  // Cursor-pagination simulation: how many rows are "loaded" per tab.
  const [loadedCount, setLoadedCount] = useState<Record<string, number>>({});
  const [loadingMore, setLoadingMore] = useState(false);
  const domain = DATA_DOMAINS.find(d => d.key === activeKey) as DataDomain;
  const allRecords = DOMAIN_RECORDS[activeKey];
  const columns = DOMAIN_COLUMNS[activeKey];
  const filters = DOMAIN_FILTERS[activeKey];
  const shown = loadedCount[activeKey] ?? INITIAL_PAGE_SIZE;
  const switchTab = (key: DomainKey) => {
    setActiveKey(key);
    setSearch('');
    setActiveFilters({});
    setSelected(null);
  };
  const toggleFilter = (category: string, value: string) => {
    setActiveFilters(prev => {
      const current = prev[category] || [];
      return {
        ...prev,
        [category]: current.includes(value) ? current.filter(v => v !== value) : [...current, value]
      };
    });
  };
  const loadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setLoadedCount(prev => ({
        ...prev,
        [activeKey]: allRecords.length
      }));
      setLoadingMore(false);
    }, 700);
  };

  // --- Filtering (keyword tabs + loaded-records scope) ---------------
  const hasActiveFilters = Object.values(activeFilters).some(v => v.length > 0);
  const filtered = allRecords.slice(0, shown).filter(record => {
    if (search && !domain.semantic) {
      const haystack = (record.title + ' ' + Object.values(record.cells).join(' ')).toLowerCase();
      if (!haystack.includes(search.toLowerCase())) return false;
    }
    if (!hasActiveFilters) return true;
    for (const [category, values] of Object.entries(activeFilters)) {
      if (values.length === 0) continue;
      const recordValues = record.meta[category] || [];
      if (!values.some(v => recordValues.includes(v))) return false;
    }
    return true;
  });

  // --- Semantic search (Confluence / Drive) --------------------------
  const semanticMode = domain.semantic && search.trim().length > 0;
  const hits = semanticMode ? semanticResults(activeKey, search) : [];
  const allLoaded = shown >= allRecords.length;
  const banner = STALE_BANNERS[activeKey];
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-795">
      <div className="max-w-[1240px] mx-auto w-full flex flex-col" data-id="element-796">
        {/* Header */}
        <header className="mb-6" data-id="element-797">
          <h1 className="text-[20px] font-bold text-[#e8e4df] tracking-tight mb-1" data-id="element-798">Data</h1>
          <p className="text-[#8a8680] text-[14px]" data-id="element-799">
            Every record synced into ZenBot — browse, search, and link back to the source.
          </p>
        </header>

        {/* Domain tabs */}
        <nav className="flex items-center gap-6 mb-5 border-b border-[#8a8680]/10 overflow-x-auto" data-id="element-800">
          {DATA_DOMAINS.map(d => {
          const isActive = activeKey === d.key;
          const Icon = DOMAIN_ICONS[d.key];
          return <button key={d.key} onClick={() => switchTab(d.key)} className={`pb-3 text-[14px] font-medium transition-colors relative flex items-center gap-2 whitespace-nowrap ${isActive ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-801">
                <Icon size={15} style={{
              color: isActive ? d.color : undefined
            }} data-id="element-802" />
                {d.label}
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-[#252530] text-[#e8e4df]' : 'bg-[#252530]/60 text-[#8a8680]/60'}`} data-id="element-803">
                  {d.total.toLocaleString()}
                </span>
                {d.stale && <AlertTriangle size={12} className="text-[#e8a84c]" data-id="element-804" />}
                {isActive && <motion.div layoutId="dataTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px]" style={{
              background: d.color
            }} initial={false} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-805" />}
              </button>;
        })}
        </nav>

        {/* Stale sync banner */}
        {banner && <div className="flex items-center gap-2.5 bg-[#e8a84c]/10 border border-[#e8a84c]/20 rounded-[12px] px-4 py-2.5 mb-5" data-id="element-806">
            <AlertTriangle size={14} className="text-[#e8a84c] shrink-0" data-id="element-807" />
            <span className="text-[13px] text-[#e8a84c]" data-id="element-808">{banner}</span>
          </div>}

        {/* Search — keyword on most tabs, semantic on content-indexed tabs */}
        <div className="relative mb-5" data-id="element-809">
          {domain.semantic ? <Sparkles size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4a9a9a]" data-id="element-810" /> : <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8680]/50" data-id="element-811" />}
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={domain.semantic ? `Search ${domain.label} by meaning — “clinical safety requirements”, “how do we rotate certs”…` : `Search ${domain.label.toLowerCase()} records…`} className="w-full bg-[#1c1c22] text-[#e8e4df] rounded-[12px] pl-11 pr-32 py-3 text-[14px] outline-none placeholder:text-[#8a8680]/40 focus:ring-1 focus:ring-[#4a9a9a]/30" data-id="element-812" />
          {domain.semantic && <span className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-[11px] text-[#4a9a9a] bg-[#4a9a9a]/10 px-2 py-1 rounded-full" data-id="element-813">
              <Sparkles size={10} data-id="element-814" />
              Semantic search
            </span>}
        </div>

        {/* Filter pills (hidden during semantic search — results are ranked, not filtered) */}
        {!semanticMode && <FilterPills filters={filters} activeFilters={activeFilters} onToggle={toggleFilter} onClear={() => setActiveFilters({})} data-id="element-815" />}

        {/* Results */}
        {semanticMode ? <SemanticResults hits={hits} accentColor={domain.color} query={search} onSelect={hit => setSelected(hit.record)} data-id="element-816" /> : <>
            <DataTable columns={columns} records={filtered} accentColor={domain.color} onSelect={setSelected} data-id="element-817" />

            {/* Pagination footer — mirrors cursor-based load-more */}
            <div className="flex items-center justify-between mt-4" data-id="element-818">
              <span className="text-[13px] text-[#8a8680]/60" data-id="element-819">
                Showing {Math.min(filtered.length, shown)} of {domain.total.toLocaleString()}{' '}
                {domain.label.toLowerCase()} records
                {hasActiveFilters && ' (filtered, loaded scope)'}
              </span>
              <button onClick={loadMore} disabled={allLoaded || loadingMore} className={`px-5 py-2 rounded-[10px] text-[13px] font-medium transition-colors ${allLoaded ? 'bg-[#1c1c22] text-[#8a8680]/40 cursor-default' : 'bg-[#252530] text-[#e8e4df] hover:bg-[#2a2a35]'}`} data-id="element-820">
                {loadingMore ? 'Loading…' : allLoaded ? 'All loaded records shown' : 'Load more'}
              </button>
            </div>
          </>}
      </div>

      {/* Row detail slide-over */}
      <RecordPanel record={selected} domain={domain} onClose={() => setSelected(null)} data-id="element-821" />
    </div>;
}